// Saved-view preset chips (phase 24) — a slim row adjacent to the FilterBar
// on Finance and Projects. Clicking a chip applies its params; the chip
// whose params match the live state gets the teal interactive treatment.
// "Save current view…" appears only when filters are active and unsaved.
// Dense by design: chips, not a management screen (rename = double-click,
// delete = × on hover).

import { useCallback, useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { useIsMobile } from '../lib/useIsMobile';
import { paramsEqual, type ViewParams } from '../../shared/savedViews';

export interface SavedViewItem {
  id: string;
  name: string;
  params: ViewParams;
}

export function ViewChips({
  page,
  current,
  isDefault,
  allowSave = true,
  leading,
  onApply,
}: {
  /** saved_views.page key ('finance' | 'projects'). */
  page: string;
  /** Live filter/search/tab state, serializable form. */
  current: Record<string, string | null | undefined>;
  /** True when the page is in its untouched default state. */
  isDefault: boolean;
  /** Transient deep-link filters can be visible without becoming presets. */
  allowSave?: boolean;
  /** Optional desktop filters, before saved views, also visible with no presets. */
  leading?: ReactNode;
  onApply: (params: ViewParams) => void;
}) {
  const isMobile = useIsMobile();
  const [viewsList, setViewsList] = useState<SavedViewItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(() => {
    api<{ views: SavedViewItem[] }>(`/api/admin/views?page=${page}`)
      .then((d) => setViewsList(d.views))
      .catch(() => {});
  }, [page]);
  useEffect(() => {
    load();
    window.addEventListener('ss:views-changed', load);
    return () => window.removeEventListener('ss:views-changed', load);
  }, [load]);

  const matches = (v: SavedViewItem) => paramsEqual(current, v.params);
  const anyMatch = viewsList.some(matches);

  const save = async () => {
    const name = draft.trim();
    setSaving(false);
    setDraft('');
    if (!name) return;
    await api('/api/admin/views', { method: 'POST', json: { page, name, params: current } }).catch(
      (error) => reportError(error, 'Could not save the view.'),
    );
  };

  const rename = async (id: string) => {
    const name = draft.trim();
    setRenaming(null);
    setDraft('');
    if (!name) return;
    await api(`/api/admin/views/${id}`, { method: 'PATCH', json: { name } })
      .catch((error) => reportError(error, 'Could not rename the view.'));
  };

  const remove = async (id: string) => {
    await api(`/api/admin/views/${id}`, { method: 'DELETE' })
      .catch((error) => reportError(error, 'Could not delete the view.'));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>, commit: () => void) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') {
      setSaving(false);
      setRenaming(null);
      setDraft('');
    }
  };

  if (viewsList.length === 0 && isDefault && !leading) return null;

  // ≤880px (§0.5.1): the same chip row as Tabs, in the accent variant and
  // starred, so a saved view reads as a preset rather than a tab. Pages
  // merge the two rows in Phases A–D. Rename (double-tap) and save stay;
  // the × delete keeps its desktop hover-reveal, so it is desktop-only.
  if (isMobile) {
    return (
      <div className="mtabs view-chips-m">
        {viewsList.map((v) =>
          renaming === v.id ? (
            <input
              key={v.id}
              className="view-chip-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => onKey(e, () => rename(v.id))}
              onBlur={() => rename(v.id)}
              autoFocus
            />
          ) : (
            <button
              key={v.id}
              type="button"
              className={`t acc${matches(v) ? ' on' : ''}`}
              onClick={() => onApply(v.params)}
              onDoubleClick={() => {
                setDraft(v.name);
                setRenaming(v.id);
              }}
            >
              ★ {v.name}
            </button>
          ),
        )}
        {allowSave && !isDefault && !anyMatch && !saving && (
          <button type="button" className="t save" onClick={() => setSaving(true)}>
            + Save view…
          </button>
        )}
        {allowSave && saving && (
          <input
            className="view-chip-input"
            placeholder="View name…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => onKey(e, save)}
            onBlur={save}
            autoFocus
          />
        )}
      </div>
    );
  }

  return (
    <div className="view-chips">
      {leading}
      {viewsList.map((v) =>
        renaming === v.id ? (
          <input
            key={v.id}
            className="view-chip-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => onKey(e, () => rename(v.id))}
            onBlur={() => rename(v.id)}
            autoFocus
          />
        ) : (
          <button
            key={v.id}
            type="button"
            className={`view-chip${matches(v) ? ' active' : ''}`}
            title="Apply view · double-click to rename"
            onClick={() => onApply(v.params)}
            onDoubleClick={() => {
              setDraft(v.name);
              setRenaming(v.id);
            }}
          >
            {v.name}
            <span
              className="x"
              title="Delete view"
              onClick={(e) => {
                e.stopPropagation();
                remove(v.id);
              }}
            >
              ×
            </span>
          </button>
        ),
      )}
      {allowSave && !isDefault && !anyMatch && !saving && (
        <button type="button" className="view-chip save" onClick={() => setSaving(true)}>
          + Save current view…
        </button>
      )}
      {allowSave && saving && (
        <input
          className="view-chip-input"
          placeholder="View name…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => onKey(e, save)}
          onBlur={save}
          autoFocus
        />
      )}
    </div>
  );
}
